"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import {
  ALL_TIERS,
  INDUSTRY_TEMPLATES,
  TIER_LABELS,
  type UserTier,
} from "@virtuous/shared";
import {
  clearViewPrefs,
  getEffectiveUser,
  getStoredUser,
  getViewPrefs,
  hasViewOverride,
  setViewPrefs,
  type StoredUser,
} from "@/lib/session";

type Props = {
  /** compact = header strip; card = home page panel */
  variant?: "compact" | "card";
  className?: string;
};

/**
 * Same-account view toggle — switch industry home and (optionally) UI tier
 * without creating more accounts. Real tier still gates Earn / security APIs.
 */
export function AccountViewSwitcher({
  variant = "card",
  className = "",
}: Props) {
  const router = useRouter();
  const [account, setAccount] = useState<StoredUser | null>(null);
  const [industry, setIndustry] = useState("general");
  const [tier, setTier] = useState<string>("");
  const [overridden, setOverridden] = useState(false);

  function refresh() {
    const u = getStoredUser();
    const eff = getEffectiveUser();
    const v = getViewPrefs();
    setAccount(u);
    setIndustry(eff?.industry || u?.industry || "general");
    setTier(
      v.tier != null
        ? String(v.tier)
        : u?.tier != null
          ? String(u.tier)
          : "",
    );
    setOverridden(hasViewOverride());
  }

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener("virtuous:view-change", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("virtuous:view-change", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  if (!account) return null;

  function applyIndustry(next: string) {
    // Clear override when selecting the account’s real industry
    if (account?.industry === next) {
      setViewPrefs({ industry: null });
    } else {
      setViewPrefs({ industry: next });
    }
    setIndustry(next);
    setOverridden(hasViewOverride());
    router.push(`/home/${next}`);
  }

  function applyTier(next: string) {
    const real = account?.tier;
    // Selecting the real account tier clears the tier override
    if (real != null && Number(next) === Number(real)) {
      setViewPrefs({ tier: null });
    } else {
      setViewPrefs({ tier: Number(next) });
    }
    setTier(next);
    setOverridden(hasViewOverride());
  }

  function reset() {
    clearViewPrefs();
    const u = getStoredUser();
    setIndustry(u?.industry || "general");
    setTier(u?.tier != null ? String(u.tier) : "");
    setOverridden(false);
    if (u?.industry) router.push(`/home/${u.industry}`);
  }

  const selectClass =
    "rounded-lg border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-xs text-[var(--text)] max-w-full";

  const body = (
    <>
      <div
        className={
          variant === "card"
            ? "space-y-3"
            : "view-switcher-compact"
        }
      >
        <label className="flex min-w-0 flex-col gap-0.5 text-left">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--faint)]">
            Industry view
          </span>
          <select
            className={selectClass}
            value={industry}
            onChange={(e) => applyIndustry(e.target.value)}
            aria-label="Switch industry view"
          >
            {INDUSTRY_TEMPLATES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex min-w-0 flex-col gap-0.5 text-left">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--faint)]">
            UI tier preview
          </span>
          <select
            className={selectClass}
            value={tier}
            onChange={(e) => applyTier(e.target.value)}
            aria-label="Preview UI as tier"
          >
            {ALL_TIERS.map((t) => (
              <option key={t} value={String(t)}>
                {/* Short labels in compact header so layout does not explode */}
                {variant === "compact"
                  ? `Tier ${t}`
                  : TIER_LABELS[t as UserTier]}
              </option>
            ))}
          </select>
        </label>

        {overridden ? (
          <button
            type="button"
            className="self-end rounded-lg border border-[var(--border)] px-2 py-1.5 text-[11px] font-semibold text-[var(--muted)] hover:border-[var(--text)] hover:text-[var(--text)]"
            onClick={reset}
          >
            Reset to my account
          </button>
        ) : null}
      </div>

      {variant === "card" ? (
        <p className="mt-2 text-[11px] leading-snug text-[var(--faint)]">
          One login — switch views here instead of making extra accounts.{" "}
          <strong>UI tier preview</strong> only shows places for that tier
          (no leftover vault / earn / leadership links for other tiers). Real
          Tier {account.tier ?? "—"} still applies if you open a locked tool
          URL directly.
        </p>
      ) : null}

      {overridden && variant === "compact" ? (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
          View mode
        </span>
      ) : null}
    </>
  );

  if (variant === "compact") {
    return (
      <div
        className={`flex flex-wrap items-end gap-2 ${className}`}
        data-view-switcher
      >
        {body}
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border border-[var(--border)] bg-[var(--bg-soft)] p-4 ${className}`}
      data-view-switcher
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-title">Account views</h2>
        {overridden ? (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
            Viewing as override
          </span>
        ) : (
          <span className="text-[10px] text-[var(--faint)]">
            Default · {account.industry.replace(/_/g, " ")} · Tier{" "}
            {account.tier ?? "—"}
          </span>
        )}
      </div>
      {body}
    </div>
  );
}
